import { ProviderData } from "@/types/provider";
import { 
  Wallet, 
  Clock, 
  TrendingUp, 
  CheckCircle2, 
  BadgeCheck, 
  Shield,
  Percent
} from "lucide-react";
import lendoLogo from "@/assets/Lendo_stor.webp";
import ucLogo from "@/assets/UC.png";
import ucImage from "@/assets/UC-3.png";
import bankTransferIcon from "@/assets/banköverföring.png";

export const lendoData: ProviderData = {
  id: "lendo",
  name: "Lendo",
  slug: "lendo",
  logo: lendoLogo,
  type: "comparison",
  numberOfLenders: 40,
  
  trustpilot: {
    rating: 4.4,
    reviews: 3373,
    url: "https://se.trustpilot.com/review/lendo.se"
  },
  
  hero: {
    badges: [
      { text: "Jämför 40 långivare med en ansökan", variant: "default" }
    ],
    description: "Lendo är Sveriges största jämförelsetjänst för lån. Genom Lendo kan du enkelt jämföra privatlån från 40 olika banker och långivare med endast en ansökan. Få upp till 10 erbjudanden och välj det bästa för dig.",
    ctaText: "Jämför lån hos Lendo",
    ctaButtonText: "Ansök hos Lendo",
    ctaButtonVariant: "default"
  },
  
  stats: [
    {
      icon: Wallet,
      value: "800 000 kr",
      label: "Maxbelopp",
      description: "Från 10 000 kr"
    },
    {
      icon: Clock,
      value: "Samma dag",
      label: "Svar på ansökan",
      description: "Snabb hantering"
    },
    {
      icon: TrendingUp,
      value: "5,07 - 33,62%",
      label: "Effektiv ränta",
      description: "Individuell prissättning"
    },
    {
      icon: CheckCircle2,
      value: "40",
      label: "Långivare",
      description: "Jämför många alternativ"
    },
    {
      icon: BadgeCheck,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Hos vissa långivare"
    },
    {
      image: ucImage,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    }
  ],
  
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC",
      description: "Endast en UC-förfrågan trots flera erbjudanden"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring",
      description: "1-3 bankdagar efter signering"
    },
    {
      icon: Percent,
      title: "Effektiv ränta",
      value: "5,07 - 33,62%",
      description: "Individuell prissättning från långivarna"
    },
    {
      icon: CheckCircle2,
      title: "Betalningsanmärkningar",
      value: "Vissa långivare godkänner",
      description: "Beror på långivarens bedömning"
    },
    {
      icon: Wallet,
      title: "Lånebelopp",
      value: "10 000 - 800 000 kr",
      description: "Stort spannet för olika behov"
    },
    {
      icon: Clock,
      title: "Återbetalningstid",
      value: "1-15 år",
      description: "Flexibel löptid"
    }
  ],
  
  termsAndConditions: [
    { term: "Lånebelopp", value: "10 000 – 800 000 kr" },
    { term: "Nominell ränta", value: "4,95 – 22,00 %" },
    { term: "Effektiv ränta", value: "5,07 – 33,62 %" },
    { term: "Löptid", value: "1 – 15 år" },
    { term: "Uppläggningsavgift", value: "(0–995 kr) Varierar mellan långivare" },
    { term: "Lån utan UC", value: "Nej" },
    { term: "Svar på ansökan", value: "Samma dag" },
    { term: "Betalningslösning", value: "Banköverföring" }
  ],
  
  loanRequirements: [
    { requirement: "Minsta ålder", value: "18 år" },
    { requirement: "Legitimering", value: "Nej" },
    { requirement: "Folkbokförd i Sverige", value: "Ja, minst 1 år" },
    { requirement: "Svenskt personnummer", value: "Ja" },
    { requirement: "Svenskt mobilnummer", value: "Ja" },
    { requirement: "Svenskt kontonummer", value: "Ja" },
    { requirement: "Minsta årsinkomst", value: "120 000 kr" },
    { requirement: "Betalningsanmärkning OK?", value: "Ja" },
    { requirement: "Ålderskrav", value: "18 +" }
  ],
  
  features: {
    pros: [
      "Samarbetar med 40 olika banker och långivare",
      "Erbjuder flera olika slags lån",
      "Omfattande support"
    ],
    cons: [
      "Inget samarbete med storbankerna"
    ]
  },
  
  interestRateSection: {
    title: "Lendos ränta",
    description: "Att ansöka om lån via Lendo är kostnadsfritt. Räntan och månadskostnaden på ditt lån bestäms inte av Lendo utan av deras samarbetspartners. När du gjort en låneansökan via Lendo bedömer långivarna din kreditvärdighet utifrån den kreditupplysning som görs hos UC, och lånevillkoren blir sedan individuellt anpassade. De tittar till exempel på din betalningsförmåga och kreditrisk.",
    rateTable: [
      { label: "Lånebelopp (lägsta – högsta)", value: "10 000 – 800 000 kr" },
      { label: "Nominell ränta", value: "4,95 – 22,00 %" },
      { label: "Effektiv ränta", value: "5,07 – 33,62 %" },
      { label: "Uppläggningsavgift", value: "(0–995 kr) Varierar mellan långivare" },
      { label: "Fakturaavgift (Pappersfaktura)", value: "Varierar mellan långivare" },
      { label: "Fakturaavgift (Autogiro)", value: "Varierar mellan långivare" }
    ]
  },
  
  payoutSection: {
    title: "Lendos utbetalning",
    description: "När du ansöker om lån via Lendo varierar återbetalningstiden beroende på vilken långivare du väljer och hur du signerar avtalet. Men räkna med att det tar cirka 2–5 arbetsdagar för pengarna att komma in på ditt konto.",
    payoutTable: [
      { timeframe: "Beviljad före 12:00", duration: "1-4 dagar" },
      { timeframe: "Beviljad efter 12:00", duration: "1-4 dagar" }
    ],
    disclaimer: "Utbetalningar sker endast under bankdagar. Om du ansökar eller får godkännande på en helg eller helgdag kan det dröja längre innan pengarna finns tillgängliga på ditt konto."
  },
  
  aboutSections: [
    {
      title: "Om Lendo",
      content: [
        "Lendo är Sveriges största jämförelsetjänst för lån och grundades 2007. Genom Lendo kan du enkelt jämföra privatlån från 40 olika banker och långivare med endast en ansökan.",
        "Lendo fungerar som en förmedlare mellan dig och långivarna. När du ansöker om ett lån via Lendo skickas din ansökan till flera av deras samarbetspartners samtidigt. Du får sedan upp till 10 erbjudanden att välja mellan, vilket gör det lätt att hitta det bästa lånet för just dina behov."
      ]
    },
    {
      title: "Långivare hos Lendo",
      content: [
        "Lendo samarbetar med 40 olika långivare, däribland både traditionella banker och moderna fintechbolag. Detta ger dig som låntagare en bred palett av alternativ att välja mellan.",
        "Observera att de stora storbankerna (Nordea, SEB, Swedbank, Handelsbanken) inte finns med bland Lendos samarbetspartners, men däremot samarbetar de med många andra etablerade och konkurrenskraftiga långivare."
      ]
    },
    {
      title: "Hur fungerar Lendo?",
      content: [
        "Processen är enkel: Du fyller i en ansökan på Lendos hemsida där du anger önskat lånebelopp och återbetalningstid. Lendo gör sedan en kreditupplysning hos UC och skickar din ansökan till relevanta långivare.",
        "Du får sedan erbjudanden från flera långivare som du kan jämföra. Det fina är att det bara blir en UC-förfrågan trots att flera långivare bedömer din ansökan. När du har valt ett erbjudande signerar du avtalet digitalt och pengarna betalas ut inom 1-3 bankdagar."
      ]
    }
  ],
  
  faq: [
    {
      question: "Accepterar långivarna hos Lendo betalningsanmärkningar?",
      answer: "Ja, ett fåtal långivare hos Lendo kan acceptera betalningsanmärkningar om de bedömer att du har bra nuvarande betalningsförmåga. Möjligheten att bli godkänd varierar mellan olika långivare och beror på din individuella ekonomiska situation."
    },
    {
      question: "Kan man låna utan UC via Lendo?",
      answer: "Nej, Lendo använder UC för kreditupplysningar, vilket påverkar din UC-Score. Om du vill undvika detta kan du istället se vår jämförelse av lån utan UC."
    },
    {
      question: "När betalas lånet ut?",
      answer: "Utbetalningstiden hos de långivare som Lendo samarbetar med är vanligtvis 1 till 3 bankdagar efter att du signerat lånekontraktet. Tidpunkten för utbetalningen beror på vilken långivare som godkänner ditt lån och när ansökan blir godkänd. Utbetalningar sker endast under bankdagar."
    },
    {
      question: "Kan jag ansöka om privatlån utan fast inkomst?",
      answer: "Ja, du kan ansöka om ett privatlån utan att ha en fast inkomst. Var dock medveten om att räntan kan bli högre då du anses som en större risk. Utefter den bedömning som bankerna gör av din profil kan du bli beviljad ett lån upp till 350 000 kr. För att få ett bättre erbjudande kan det vara värt att vänta med din låneansökan om du vet med dig att din ekonomiska situation kommer att förändras den närmaste tiden."
    },
    {
      question: "Kostar det något att använda Lendo?",
      answer: "Nej, för dig som ansöker om ett lån är det gratis att använda Lendo. Hela deras provision tas i stället som ersättning från den långivare som ditt lån förmedlades till. Det kan dock tillkomma kostnader hos långivaren du väljer att teckna lånet hos."
    },
    {
      question: "Vilka typer av lån jämför Lendo?",
      answer: "Lendo jämför privatlån, bolån och företagslån. Alla deras privatlån är utan säkerhet och det spelar därför ingen roll i vilket syfte du ansöker om lånet."
    },
    {
      question: "Kan man samla sina lån hos Lendo?",
      answer: "Ja, Lendo kan hjälpa dig att samla dina lån genom att jämföra erbjudanden från flera långivare. Detta kan hjälpa dig att få bättre översikt och potentiellt lägre räntekostnader."
    },
    {
      question: "Vilken ränta kan man få genom Lendo?",
      answer: "Räntan varierar mellan 5,07% och 33,62% i effektiv ränta beroende på din kreditvärdighet, lånebelopp och återbetalningstid. Den exakta räntan sätts av långivarna som Lendo samarbetar med baserat på din individuella ekonomiska situation."
    },
    {
      question: "Kan man ansöka om ett EU-lån?",
      answer: "Ja, du kan ta EU-lån hos Lendo. Det är ett lån som syftar till att stötta europeiska småföretagare. Du kan låna upp till 500 000 kronor. En av fördelarna med ett EU-lån är att du behöver gå i personlig borgen för endast 20 procent av lånebeloppet."
    },
    {
      question: "Vilka är grundkraven för att ta ett privatlån?",
      answer: "På Lendo kan du ansöka om lån upp till 800 000 kronor. För lån över 30 000 kr måste du vara minst 18 år, folkbokförd och skriven i Sverige minst ett år, ha en fast inkomst från arbete eller pension, en årsinkomst på minst 120 000 kr, och vara skuldfri från Kronofogden i minst 6 månader. Om du arbetar extra timmar, har ett vikariat eller är provanställd kan du ansöka om lån upp till 100 000 kr. Vid en tidsbegränsad anställning är det viktigt att du har 6 månader kvar av din anställningstid."
    }
  ],
  
  customerService: {
    description: "Lendos kundtjänst finns tillgänglig för att hjälpa dig med frågor om din låneansökan och jämförelsetjänsten. Du kan nå dem via telefon, e-post eller chatt.",
    phone: "0771-131310",
    phoneHours: [
      "Vardagar: 08.00-20.00",
      "Lördag: 10.00-14.00",
      "Söndag: 10.00-14.00"
    ],
    email: "kundservice@lendo.se",
    emailHours: ["Svarar inom 24 timmar"]
  },
  
  companyInfo: {
    name: "Lendo AB",
    orgNumber: "556515-1627",
    address: "Västra Järnvägsgatan 21",
    city: "111 64 Stockholm",
    website: "https://www.lendo.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=72235"
  },
  
  affiliateLink: {
    linkId: "lendo-affiliate-link-id",
    trackingSource: "lendo_page",
    url: "https://go.adt228.com/t/t?a=1060519344&as=2024680921&t=2&tk=1"
  },
  
  seo: {
    title: "Lendo Privatlån 2025 - Jämför 40 Långivare | Från 10 000 kr",
    description: "Lendo har varit aktiva sedan 2007. Jämför privatlån från 40 långivare med en ansökan. Låna 10 000-800 000 kr med effektiv ränta 5,07-33,62%. Svar samma dag. Läs mer!",
    keywords: [
      "lendo",
      "privatlån",
      "jämför lån",
      "låneförmedlare",
      "40 långivare",
      "samlingslån",
      "billån",
      "bolån"
    ]
  },
  
  schema: {
    productName: "Lendo Privatlån - Jämför 40 långivare",
    description: "Jämför privatlån från 40 olika långivare med en ansökan. Belopp 10 000 - 800 000 kr. Svar samma dag. Erbjuder även billån, bolån, företagslån och samlingslån.",
    url: "https://klarfinans.se/sv-SE/privatlan/lendo",
    provider: "Lendo",
    interestRate: {
      min: 5.07,
      max: 33.62
    },
    minAmount: 10000,
    maxAmount: 800000,
    loanTerm: ["1 år", "2 år", "3 år", "5 år", "10 år", "15 år"],
    featureList: [
      "Jämför lån från 40 olika långivare med en ansökan",
      "Lån från 10 000 kr upp till 800 000 kr",
      "Svar samma dag",
      "Flexibla återbetalningsvillkor 1-15 år",
      "Effektiv ränta 5,07 – 33,62 %",
      "Kan acceptera betalningsanmärkningar hos vissa långivare",
      "Erbjuder även billån, bolån, företagslån och samlingslån",
      "Endast en UC-kreditupplysning trots flera erbjudanden"
    ]
  }
};
